import { Observable } from 'rxjs';

/**
 * Contract for data source adapters.
 * Implement this interface to create custom data sources.
 *
 * The adapter pattern normalizes different data sources (static, observable, paginated)
 * into a consistent interface the table component can consume.
 *
 * ## Built-in Adapters
 * - `StaticDataSource` - Wraps T[]
 * - `ObservableDataSource` - Wraps Observable<T[]>
 * - `PaginatedDataSourceAdapter` - Wraps PaginatedDataService
 *
 * ## Custom Adapter Example
 * ```ts
 * class WebSocketDataSource<T> implements DataSourceAdapter<T> {
 *   readonly type = 'custom' as const;
 *   private socket: WebSocket;
 *   private data$ = new BehaviorSubject<T[]>([]);
 *
 *   connect(): Observable<T[]> {
 *     this.socket = new WebSocket('ws://...');
 *     this.socket.onmessage = (e) => this.data$.next(JSON.parse(e.data));
 *     return this.data$;
 *   }
 *
 *   disconnect(): void {
 *     this.socket?.close();
 *   }
 * }
 * ```
 *
 * @template T - Row data type
 */
export interface DataSourceAdapter<T> {
  /**
   * Adapter type identifier. Used by table to determine behavior:
   * - 'static': No pagination/sorting delegation
   * - 'observable': Reactive updates, client-side operations
   * - 'paginated': Server-side pagination and sorting
   * - 'custom': Your custom implementation
   */
  readonly type: 'static' | 'observable' | 'paginated' | 'custom';

  /**
   * Returns an observable of the current data.
   * Called once during initialization; the table subscribes and updates on emissions.
   */
  connect(): Observable<T[]>;

  /**
   * Cleanup method called when table is destroyed or dataSource changes.
   * Use this to unsubscribe, close connections, etc.
   */
  disconnect(): void;

  /**
   * Optional: Total count for pagination display.
   * Required for server-side pagination to show "Page X of Y".
   */
  totalCount$?: Observable<number>;

  /**
   * Optional: Loading state observable.
   * Can be used by parent components for loading indicators.
   */
  loading$?: Observable<boolean>;

  /**
   * Optional: Current page index for paginator sync.
   * Used to restore paginator state after breadcrumb navigation.
   */
  pageIndex$?: Observable<number>;

  /**
   * Optional: Current page size for paginator sync.
   * Used to restore paginator state after breadcrumb navigation.
   */
  pageSize$?: Observable<number>;

  /**
   * Optional: Called when user changes page.
   * Only implemented by paginated adapters; static/observable handle client-side.
   */
  paginate?(page: number, pageSize: number): void;

  /**
   * Optional: Called when user clicks sort header.
   * For 'paginated' type, this triggers a server request.
   * For other types, MatTableDataSource handles client-side sorting.
   */
  sort?(field: string, direction: 'asc' | 'desc'): void;

  /**
   * Optional: Apply filter/search criteria.
   * Implementation depends on adapter type.
   */
  filter?(criteria: any): void;
}
